import { get, patch, post } from '@/services/http';
import {
  InventoryTransferModel,
  InventoryTransferInterface,
  CreateTransferRequest,
  CreateTransferAPIResponse,
} from '@/models/inventory_transfer';
import List from './list';

export interface InventoryTransferFilters {
  status?: string;
  sourceLocationId?: number;
  destinationLocationId?: number;
}

export class InventoryTransfers extends List<InventoryTransferModel> {
  public total: number = 0;
  public loading: boolean = false;
  public error: string | null = null;

  public mapData(data: InventoryTransferInterface[] = []): void {
    this.items = [];
    for (const item of data) {
      const transfer = new InventoryTransferModel().mapData(item);
      this.add(transfer);
    }
    this.total = this.size();
  }

  public async fetch(filters: InventoryTransferFilters = {}): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const params: any = {};
      if (filters.status) { params.status = filters.status; }
      if (filters.sourceLocationId) { params.source_location_id = filters.sourceLocationId; }
      if (filters.destinationLocationId) { params.destination_location_id = filters.destinationLocationId; }

      const response: any = await get('/inventory-transfers', params);
      const transfersArray = response.transfers || response; // Handle both formats
      this.mapData(transfersArray);
      this.total = this.items.length;
    } catch (error: any) {
      this.error = error.message || 'Failed to fetch transfers';
      this.items = [];
      this.total = 0;
    } finally {
      this.loading = false;
    }
  }

  public async getById(transferId: number): Promise<InventoryTransferModel | null> {
    try {
      const response: any = await get(`/inventory-transfers/${transferId}`);
      return new InventoryTransferModel().mapData(response);
    } catch (error: any) {
      this.error = error.message || 'Failed to fetch transfer details';
      return null;
    }
  }

  public async markAsShipped(transferId: number): Promise<InventoryTransferModel | null> {
    try {
      const response: any = await patch(`/inventory-transfers/${transferId}/ship`);

      const updatedTransfer = new InventoryTransferModel();
      updatedTransfer.mapData(response);

      // Update the item in the local collection if it exists
      const index = this.items.findIndex((item) => item.id === transferId);
      if (index !== -1) {
        this.items.splice(index, 1, updatedTransfer);
      }

      return updatedTransfer
    } catch (error: any) {
      this.error = error.message || 'Failed to mark transfer as shipped';
      return null;
    }
  }

  public async markAsReceived(transferId: number): Promise<InventoryTransferModel | null> {
    try {
      const response: any = await patch(`/inventory-transfers/${transferId}/receive`);

      const updatedTransfer = new InventoryTransferModel();
      updatedTransfer.mapData(response);

      // Update the item in the local collection if it exists
      const index = this.items.findIndex((item) => item.id === transferId);
      if (index !== -1) {
        this.items.splice(index, 1, updatedTransfer);
      }

      return updatedTransfer
    } catch (error: any) {
      this.error = error.message || 'Failed to mark transfer as received';
      return null;
    }
  }

  public async markAsReceivedCustom(transferId: number, receiveData: any): Promise<InventoryTransferModel | null> {
    try {
      const response: any = await patch(`/inventory-transfers/${transferId}/receive`, receiveData);

      const updatedTransfer = new InventoryTransferModel();
      updatedTransfer.mapData(response);

      // Update the item in the local collection if it exists
      const index = this.items.findIndex((item) => item.id === transferId);
      if (index !== -1) {
        this.items.splice(index, 1, updatedTransfer);
      }

      return updatedTransfer
    } catch (error: any) {
      this.error = error.message || 'Failed to mark transfer as received';
      return null;
    }
  }

  public async forceCompleteTransfer(transferId: number, data: { reason: string; final_note?: string }): Promise<InventoryTransferModel | null> {
    this.error = null;

    try {
      const response: any = await post(`/inventory-transfers/${transferId}/force-complete`, data);

      const updatedTransfer = new InventoryTransferModel();
      updatedTransfer.mapData(response);

      // Update the item in the local collection if it exists
      const index = this.items.findIndex((item) => item.id === transferId);
      if (index !== -1) {
        this.items.splice(index, 1, updatedTransfer);
      }

      return updatedTransfer
    } catch (error: any) {
      this.error = error.message || 'Failed to force complete transfer';
      return null;
    }
  }

  public get pendingTransfers(): InventoryTransferModel[] {
    return this.items.filter((transfer) => transfer.status === 'PENDING');
  }

  public get shippedTransfers(): InventoryTransferModel[] {
    return this.items.filter((transfer) => transfer.status === 'SHIPPED');
  }

  public get partialReceivedTransfers(): InventoryTransferModel[] {
    return this.items.filter((transfer) => transfer.status === 'PARTIAL_RECEIVED');
  }

  public get completedTransfers(): InventoryTransferModel[] {
    return this.items.filter((transfer) => transfer.status === 'COMPLETED');
  }

  public async createTransfer(transferData: CreateTransferRequest): Promise<InventoryTransferModel | null> {
    this.loading = true;
    this.error = null;

    try {
      const response: CreateTransferAPIResponse = await post('/inventory-transfers', transferData);
      const newTransfer = new InventoryTransferModel().mapData(response);

      // Add to local collection
      this.items.unshift(newTransfer);
      // Only update total if we're managing the full list locally
      // Otherwise, increment the existing total
      this.total = this.total > 0 ? this.total + 1 : this.size();

      return newTransfer;
    } catch (error: any) {
      this.error = error.message || 'Failed to create transfer';
      return null;
    } finally {
      this.loading = false;
    }
  }

  public async createShopifyTransfer(transferData: CreateTransferRequest): Promise<InventoryTransferModel | null> {
    this.loading = true;
    this.error = null;

    try {
      const response: any = await post('/inventory-transfers/shopify', transferData);
      const newTransfer = new InventoryTransferModel().mapData(response);
      
      // Preserve Shopify-specific properties that aren't in the model
      (newTransfer as any).name = response.name;
      (newTransfer as any).shopify_transfer_id = response.shopify_transfer_id;
      (newTransfer as any).total_quantity = response.total_quantity;
      (newTransfer as any).received_quantity = response.received_quantity;

      // Add to local collection
      this.items.unshift(newTransfer);
      // Only update total if we're managing the full list locally
      // Otherwise, increment the existing total
      this.total = this.total > 0 ? this.total + 1 : this.size();

      return newTransfer;
    } catch (error: any) {
      this.error = error.message || 'Failed to create Shopify transfer';
      return null;
    } finally {
      this.loading = false;
    }
  }

  public clearError(): void {
    this.error = null;
  }
}
